import { useState } from 'react';
import { api } from '../../api.js';
import Field from '../Field.jsx';
import Alert from '../ui/Alert.jsx';
import Modal, { ModalActions } from '../ui/Modal.jsx';
import { TYPE_HELP, TYPE_LABELS, VISIBILITY_OPTIONS } from './identityMeta.js';
import { checkBank, checkEmail, checkOwnDomain, checkPhone, checkUrl, cleanDomain, cleanedHint } from '../../utils/validate.js';

// The format each type expects (the server checks the same), and the right keyboard
const CHECKS = {
  email_domain: (v) => checkOwnDomain(v),
  web_domain: (v) => checkOwnDomain(v),
  email_address: (v) => checkEmail(v),
  phone: (v) => checkPhone(v),
  bank: (v) => checkBank(v),
  social: (v) => checkUrl(v),
  url: (v) => checkUrl(v),
};
const INPUT = {
  email_domain: { inputMode: 'url', autoCapitalize: 'none' },
  web_domain: { inputMode: 'url', autoCapitalize: 'none' },
  email_address: { type: 'email', autoCapitalize: 'none' },
  phone: { type: 'tel', inputMode: 'tel' },
  bank: { autoCapitalize: 'characters' },
  social: { inputMode: 'url', autoCapitalize: 'none' },
  url: { inputMode: 'url', autoCapitalize: 'none' },
};
const DOMAIN_TYPES = new Set(['email_domain', 'web_domain']);

/**
 * Add an identifier, or edit one's label and visibility. The value of an existing
 * identifier can't change: that would be a different identifier with its own proof
 * of ownership, so it's removed and added again instead.
 *
 * basePath: the API path of the owner's identifiers, e.g.
 *           /portal/organisations/3/entities/12/identifiers
 */
export default function IdentifierModal({ basePath, ownerName, identifier, onClose, onSaved, types, fixedVisibility, description }) {
  const allowed = types || Object.keys(TYPE_LABELS);
  const editing = !!identifier;
  const [form, setForm] = useState({
    type: identifier?.type || (types ? types[0] : 'email_domain'),
    value: '',
    label: identifier?.label || '',
    visibility: identifier?.visibility || 'public',
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const help = TYPE_HELP[form.type];

  const set = (k, v) => {
    const next = { ...form, [k]: v };
    // Bank details are never public
    if (k === 'type' && v === 'bank' && next.visibility === 'public') next.visibility = 'confirm_only';
    setForm(next);
    // A new type means a different format: the old complaint no longer applies
    if (k === 'type') setErrors({});
    else if (errors[k]) setErrors({ ...errors, [k]: undefined });
  };
  const checkValue = () => {
    if (editing || !form.value.trim()) return;
    const problem = CHECKS[form.type]?.(form.value);
    if (problem) setErrors((e) => ({ ...e, value: problem }));
    // A pasted https://www.x.com/page becomes the domain itself, before saving
    else if (DOMAIN_TYPES.has(form.type)) setForm((f) => ({ ...f, value: cleanDomain(f.value) }));
  };

  const save = async () => {
    if (!editing) {
      const problem = CHECKS[form.type]?.(form.value);
      if (problem) { setErrors({ value: problem }); return; }
    }
    setBusy(true); setError('');
    try {
      const res = editing
        ? await api(`${basePath}/${identifier.id}`, { method: 'PATCH', body: { label: form.label, visibility: form.visibility } })
        : await api(basePath, { method: 'POST', body: form });
      onSaved(res.identifier, editing);
    } catch (err) {
      setErrors(err.data?.fields || {});
      setError(err.data?.fields ? '' : err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={editing ? `Edit ${TYPE_LABELS[identifier.type].toLowerCase()}` : 'Add an official channel'}
      description={editing ? (description || undefined) : (description || `Something that genuinely belongs to ${ownerName}. Anything not listed is answered as “cannot be verified”.`)}
      onClose={onClose} onSubmit={save} busy={busy}
      footer={<ModalActions submitLabel={editing ? 'Save' : 'Add'} busyLabel={editing ? 'Saving…' : 'Adding…'} busy={busy} onCancel={onClose} />}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      {editing ? (
        <div className="readonly-value">
          <span className="label-small">{TYPE_LABELS[identifier.type]}</span>
          <span className="mono">{identifier.display_value}</span>
        </div>
      ) : (
        <>
          <Field label="Type" as="select" value={form.type} onChange={(e) => set('type', e.target.value)} error={errors.type}>
            {allowed.map((k) => <option key={k} value={k}>{TYPE_LABELS[k]}</option>)}
          </Field>
          <Field label={TYPE_LABELS[form.type]} required value={form.value} placeholder={help.placeholder}
            onChange={(e) => set('value', e.target.value)} onBlur={checkValue} error={errors.value}
            hint={!errors.value ? ((DOMAIN_TYPES.has(form.type) && cleanedHint(form.value)) || help.hint) : undefined}
            autoComplete="off" spellCheck={false} maxLength={500} {...INPUT[form.type]} />
        </>
      )}
      <Field label="Used for" optional hint="So your team (and, if it’s listed, the public) know what it’s for." value={form.label} placeholder="Registration and tickets" maxLength={200}
        onChange={(e) => set('label', e.target.value)} />
      {fixedVisibility ? <p className="muted small">{fixedVisibility}</p> : <fieldset className="fieldset">
        <legend>Who can see it</legend>
        {VISIBILITY_OPTIONS.map((o) => {
          const blocked = o.key === 'public' && form.type === 'bank';
          return (
            <label key={o.key} className={`radio-card${form.visibility === o.key ? ' is-selected' : ''}${blocked ? ' is-disabled' : ''}`}>
              <input type="radio" name="visibility" value={o.key} checked={form.visibility === o.key} disabled={blocked}
                onChange={() => set('visibility', o.key)} />
              <span><strong>{o.label}</strong><span className="muted small">{blocked ? 'Bank details are never listed publicly.' : o.hint}</span></span>
            </label>
          );
        })}
      </fieldset>}
    </Modal>
  );
}
